import { useState, useEffect, useCallback, useRef } from "react";

function postMessageToPreviewIframe(
  iframeRef: React.RefObject<HTMLIFrameElement>,
  iframeOrigin: string | undefined,
  payload: Record<string, unknown>
) {
  if (!iframeOrigin) {
    return;
  }
  const win = iframeRef.current?.contentWindow;
  if (!win) {
    return;
  }
  try {
    win.postMessage(payload, iframeOrigin);
  } catch {
    // iframe may still be about:blank (parent origin) before cross-origin navigation completes
  }
}

export function useSectionField(
  iframeRef: React.RefObject<HTMLIFrameElement>,
  iframeOrigin: string,
  selectedSection: string | null,
  targetSectionId: string,
  selector: string,
  kind: "text" | "html" | "style" | "src",
  cssProps?: string | Array<string>,
  applyToAll: boolean = false,
  mediaQuery: string = undefined,
  isImportant: boolean = false
) {
  const sectionId =
    selectedSection === targetSectionId ? targetSectionId : null;

  const matchesMediaQuery = (msgMediaQuery: string | null | undefined) =>
    (msgMediaQuery ?? null) === (mediaQuery ?? null);

  const [value, setValue] = useState<string>("");
  const valueRef = useRef(value);
  const forceIframeSyncRef = useRef(false);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const applyValueFromIframe = useCallback((nextValue: string) => {
    setValue(prev => {
      if (!forceIframeSyncRef.current && prev !== "") {
        return prev;
      }
      return nextValue;
    });
    forceIframeSyncRef.current = false;
  }, []);

  let getMsgType: string;
  let initialReplyType: string;
  let updateMsgType: string;
  let cssProp: string;

  if (kind === "text") {
    getMsgType = "getInitialText";
    initialReplyType = "initialText";
    updateMsgType = "updateText";
  } else if (kind === "html") {
    getMsgType = "getInitialHtml";
    initialReplyType = "initialHtml";
    updateMsgType = "updateHtml";
  } else if (kind === "src") {
    getMsgType = "getInitialSrc";
    initialReplyType = "initialSrc";
    updateMsgType = "updateSrc";
  } else {
    getMsgType = "getInitialStyle";
    initialReplyType = "initialStyle";
    updateMsgType = "updateStyle";
    if (!cssProps) {
      throw new Error(
        'When kind==="style", you must pass a cssProp string or an array of strings.'
      );
    } else if (typeof cssProps === "string") {
      cssProp = cssProps;
    } else {
      cssProp = cssProps[0];
    }
  }

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string) => {
      if (e === undefined || e === null) return;
      
      let newVal = typeof e === "string" ? e : e.target.value;

      if (kind === "style" && cssProp === "fontSize") {
        newVal = newVal.endsWith("px") ? newVal : newVal + "px";
      }

      setValue(newVal);

      const payload: any = {
        type: updateMsgType,
        sectionId: targetSectionId,
        selector,
        applyToAll,
      };

      if (kind === "text") {
        payload.text = newVal;
      } else if (kind === "html") {
        payload.html = newVal;
      } else if (kind === "src") {
        payload.src = newVal;
      } else {
        let styleValue;
        if (typeof cssProps === "string") {
          if (cssProps === "background-image") {
            styleValue = { [cssProps]: generateCssUrl(newVal) + (isImportant ? " !important" : "") };
          } else if (cssProps === "background") {
            styleValue = { [cssProps]: generateCssUrl(newVal) + " no-repeat center / contain" + (isImportant ? " !important" : "") };
          } else {
            styleValue = { [cssProps]: newVal + (isImportant ? " !important" : "") };
          }
        } else {
          styleValue = cssProps.reduce((styleObj, prop) => {
            styleObj[prop] = newVal + (isImportant ? " !important" : "");
            return styleObj;
          }, {});
        }
        payload.style = styleValue;
        if (mediaQuery) {
          payload.mediaQuery = mediaQuery;
        }
      }

      postMessageToPreviewIframe(iframeRef, iframeOrigin, payload);
    },
    [iframeRef, targetSectionId, selector, kind, cssProp, updateMsgType, applyToAll, mediaQuery, isImportant, cssProps, iframeOrigin]
  );

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      const msg = e.data || {};
      if (
        msg.type === initialReplyType &&
        msg.sectionId === targetSectionId &&
        msg.selector === selector &&
        matchesMediaQuery(msg.mediaQuery) &&
        (kind !== "style" || msg.prop === cssProp)
      ) {
        if (kind === "text") {
          applyValueFromIframe(msg.text || "");
        } else if (kind === "html") {
          applyValueFromIframe(msg.html || "");
        } else if (kind === "src") {
          applyValueFromIframe(msg.src || "");
        } else {
          if (msg.prop.includes("color")) {
            applyValueFromIframe(parseRGBToHex(msg.value) || "");
          } else if (msg.prop.includes("image") || msg.prop === "background") {
            const imageUrl = extractUrlFromBackgroundImage(msg.value) || "";
            if (imageUrl) {
              applyValueFromIframe(imageUrl);
            }
          } else {
            applyValueFromIframe(msg.value || "");
          }
        }
      }
    }

    window.addEventListener("message", onMessage);

    if (sectionId && valueRef.current === "") {
      const getPayload: any = { type: getMsgType, sectionId, selector, mediaQuery };
      if (kind === "style") {
        getPayload.prop = cssProp;
      }
      postMessageToPreviewIframe(iframeRef, iframeOrigin, getPayload);
    }

    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [
    iframeRef,
    sectionId,
    selector,
    kind,
    cssProp,
    getMsgType,
    initialReplyType,
    targetSectionId,
    mediaQuery,
    iframeOrigin,
    applyValueFromIframe,
  ]);

  const fetchInitialValue = useCallback(() => {
    forceIframeSyncRef.current = true;
    const getPayload: any = {
      type: getMsgType,
      sectionId: targetSectionId,
      selector,
      mediaQuery,
    };
    if (kind === "style") {
      getPayload.prop = cssProp;
    }
    postMessageToPreviewIframe(iframeRef, iframeOrigin, getPayload);
  }, [
    iframeRef,
    targetSectionId,
    selector,
    kind,
    cssProp,
    getMsgType,
    iframeOrigin,
    mediaQuery,
  ]);

  return { value, onChange, fetchInitialValue };
}

function parseRGBToHex(value: any): string {
  if (typeof value !== "string") return "";
  const rgbMatch = value.match(
    /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([0-9.]+))?/i
  );
  if (!rgbMatch) return value;

  const r = parseInt(rgbMatch[1], 10);
  const g = parseInt(rgbMatch[2], 10);
  const b = parseInt(rgbMatch[3], 10);

  const toHex = (n: number) => n.toString(16).padStart(2, "0");

  let hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`;

  if (typeof rgbMatch[4] !== "undefined") {
    let alpha = Math.round(parseFloat(rgbMatch[4]) * 255);
    hex += toHex(alpha);
  }

  return hex;
}

const extractUrlFromBackgroundImage = (backgroundImageValue: string) => {
  if (!backgroundImageValue || typeof backgroundImageValue !== "string") {
    return null;
  }

  const urlMatch = backgroundImageValue.match(/url\(['"]?(.*?)['"]?\)/i);

  return urlMatch ? urlMatch[1].trim() : null;
};

const generateCssUrl = (url: string) => {
  if (!url || typeof url !== "string") {
    return "";
  }

  const cleanUrl = url.trim();

  const escapedUrl = cleanUrl.replace(/'/g, "\\'");

  return `url('${escapedUrl}')`;
};
